// BottomNav der People-App unter `md` (DESIGN §3.1.4, UX §2.8): 4 Ziele + „More“ (Sheet von unten).
import { DotsThreeOutline } from '@phosphor-icons/react';
import { type ReactNode, useState } from 'react';
import { cn } from '../kern/cn';
import { RouterLink } from '../kern/Link';
import { type ShellNavItem } from './NavRail';
import { CountBadge, Drawer } from './parts';

export interface BottomNavProps {
  items: readonly ShellNavItem[];
  /** Inhalt des „More“-Sheets (Liste der übrigen Ziele); `close` schließt nach der Wahl. */
  more?: (close: () => void) => ReactNode;
  moreActive?: boolean;
  className?: string;
}

const entryClass = (active?: boolean) =>
  cn(
    'relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 text-nav outline-offset-[-2px]',
    active ? 'text-accent-fg' : 'text-fg-nav',
  );

export function BottomNav({ items, more, moreActive, className }: BottomNavProps) {
  const [open, setOpen] = useState(false);
  return (
    <nav
      aria-label="Main"
      className={cn(
        'fixed inset-x-0 bottom-0 z-(--bar-z-nav) flex h-[calc(var(--spacing-bottomnav)+env(safe-area-inset-bottom))] border-t border-line-subtle bg-page pb-[env(safe-area-inset-bottom)] md:hidden',
        className,
      )}
    >
      {items.map((item) => {
        const Glyph = item.icon;
        return (
          <RouterLink
            key={item.id}
            href={item.href}
            reload={item.reload}
            aria-current={item.active ? 'page' : undefined}
            aria-label={item.badge ? `${item.label}, ${item.badge} unread` : undefined}
            className={entryClass(item.active)}
          >
            <span className="relative">
              <Glyph size={24} weight="fill" aria-hidden />
              {item.badge ? (
                <CountBadge
                  count={item.badge}
                  tone={item.badgeTone}
                  className="absolute -top-1 -right-2.5 ring-2 ring-page"
                />
              ) : null}
            </span>
            {/* Ohne Innenabstand und leicht enger gesetzt: „Performance“ passt auch bei 360 (72 je Platz). */}
            <span className="max-w-full truncate tracking-[-0.01em]">{item.label}</span>
          </RouterLink>
        );
      })}
      {more ? (
        <>
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={open}
            className={entryClass(moreActive)}
            onClick={() => setOpen(true)}
          >
            <DotsThreeOutline size={24} weight="fill" aria-hidden />
            <span>More</span>
          </button>
          <Drawer open={open} onOpenChange={setOpen} side="bottom" title="More" showTitle>
            <div className="px-2 pb-4">{more(() => setOpen(false))}</div>
          </Drawer>
        </>
      ) : null}
    </nav>
  );
}
